import { useState } from "react";
import { Badge } from "@volca/ui/components/ui/badge";
import { Button } from "@volca/ui/components/ui/button";
import { Stack } from "@volca/ui/components/ui/stack";
import { Text } from "@volca/ui/components/ui/text";
import {
  getManualRowAudit,
  getManualRowDisplay,
} from "@/payroll/ai-script/ai-script-persistent-edits-utils";
import { useDeleteManualRow } from "@/payroll/ai-script/query";
import {
  type IAiSpreadsheetCellLocation,
  type IAiSpreadsheetManualRow,
  type IAiSpreadsheetSheet,
} from "@/payroll/ai-script/types";
import { InlineConfirmation } from "./inline-confirmation";
import {
  BoldValue,
  CellLocationLink,
  EditCard,
  showPersistentEditMutationError,
} from "./shared";

interface ManualRowRowProps {
  row: IAiSpreadsheetManualRow;
  businessId: string | null;
  runId: string;
  sheets:
    | Pick<
        IAiSpreadsheetSheet,
        "id" | "name" | "recipient_external_id" | "editable"
      >[]
    | undefined;
  onGoToCell: (cell: IAiSpreadsheetCellLocation) => void;
  // Fired on a successful remove (advances the server workbook version) so the
  // session undo/redo stack can be reset.
  onWorkbookMutated?: () => void;
}

// One added/manual row in the Manual edits panel. Unlike a cell override there is
// no "needs review" state — a user-added row is always applied — so the only
// action is to remove it (reverting the sheet to pure script output for that row).
export const ManualRowRow = ({
  row,
  businessId,
  runId,
  sheets,
  onGoToCell,
  onWorkbookMutated,
}: ManualRowRowProps) => {
  const del = useDeleteManualRow(businessId);
  const [confirmOpen, setConfirmOpen] = useState(false);
  // The row carries a sheet name/recipient, not an id; resolve the live sheet so
  // the delete invalidates the right cache and respects edit access.
  const sheet = sheets?.find(
    (candidate) =>
      candidate.name === row.sheet_name &&
      candidate.recipient_external_id === row.recipient_external_id,
  );
  const canDelete = !!sheet?.editable;
  const display = getManualRowDisplay(row);
  const audit = getManualRowAudit(row);

  const onDelete = () => {
    if (!sheet) return;
    del.mutate(
      { runId, sheetId: sheet.id, manualRowId: row.id },
      {
        onSuccess: () => {
          setConfirmOpen(false);
          onWorkbookMutated?.();
        },
        onError: (error) =>
          showPersistentEditMutationError("Couldn't remove the row", error),
      },
    );
  };

  return (
    <EditCard>
      <Stack
        direction="row"
        align="center"
        justify="between"
        gap={2}
        className="w-full min-w-0"
      >
        {row.cell ? (
          <CellLocationLink cell={row.cell} onGoToCell={onGoToCell} />
        ) : (
          <Text
            variant="label"
            tone="muted"
            className="truncate"
            title={row.sheet_name}
          >
            {row.sheet_name}
          </Text>
        )}
        {display.wasMoved ? (
          <Badge variant="secondary" color="warning" size="sm">
            Moved
          </Badge>
        ) : (
          <Badge variant="secondary" color="information" size="sm">
            Added row
          </Badge>
        )}
      </Stack>

      <Text className="min-w-0 truncate" title={display.label}>
        <BoldValue>{display.label}</BoldValue>
      </Text>

      {display.note ? (
        <Text tone="muted" className="wrap-break-word">
          {display.note}
        </Text>
      ) : null}

      {/* Same attribution line a cell override carries, so an added row is as
          accountable as an edited one. Two lines when the row was edited after it
          was added — that edit has no override entry of its own to show it. */}
      {audit.map((line) => (
        <Text
          key={line}
          variant="caption"
          tone="muted"
          className="truncate"
          title={line}
        >
          {line}
        </Text>
      ))}

      {/* Inline confirmation — removing a row deletes it from the sheet and can't
          be undone from here, so make the consequence explicit before removing. */}
      {confirmOpen ? (
        <InlineConfirmation
          message={
            <>
              Remove this row? It&apos;s deleted from the sheet and can&apos;t
              be recovered. You&apos;d have to add it back manually.
            </>
          }
          confirmLabel="Remove row"
          isPending={del.isPending}
          onCancel={() => setConfirmOpen(false)}
          onConfirm={onDelete}
        />
      ) : canDelete ? (
        <Stack
          align="start"
          gap={4}
          direction="row"
          justify="end"
          className="w-full pt-1"
        >
          <Button
            variant="outline"
            size="xs"
            onClick={() => setConfirmOpen(true)}
          >
            Remove
          </Button>
        </Stack>
      ) : null}
    </EditCard>
  );
};
